import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { fromJS, List, Map } from 'immutable';
import { CustomValidators } from 'ngx-custom-validators';
import { AppliedTax } from '../../../document-data/sub-models/applied-tax.model';
import {
  Discount,
  DiscountTypesEnum,
} from '../../../document-data/sub-models/discount.model';
import {
  DocumentItem,
  MarkupTypesEnum,
} from '../../../document-data/sub-models/document-item.model';
import { ISelectOption } from '../../../form-master/components/select-field/select-field.interface';
import { IValueSwitch } from '../../../form-master/components/value-switch-field/value-switch-field.component';
import { IFormMasterBuilder } from '../../../form-master/form-master.interface';
import { ItemUnitTypesEnum } from '../../../item-data/item-data.data';
import { TaxRate } from '../../../settings-data/sub-models/company-settings/tax-rate.model';
import { Tax } from '../../../settings-data/sub-models/company-settings/tax.model';
import { WithholdingTax } from '../../../settings-data/sub-models/company-settings/withholding-tax.model';
import { PercentagePipe } from '../../../shared/pipes/percentage/percentage.pipe';
import {
  ICurrencyInfo,
  MoneyService,
} from '../../../shared/services/money/money.service';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import {
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';

@Injectable()
export class DocumentItemOptionsBuilder implements IFormMasterBuilder {
  FORM_KEYS = {
    ENABLE_DISCOUNT: 'enableDiscount',
    ENABLE_MARKUP: 'enableMarkup',
    DISCOUNT_PERCENTAGE: 'discountPercentage',
    DISCOUNT_AMOUNT: 'discountAmount',
    DISCOUNT_TYPE: 'discountType',
    MARKUP_PERCENTAGE: 'markupPercentage',
    MARKUP_AMOUNT: 'markupAmount',
    MARKUP_TYPE: 'markupType',
    ENABLE_WITHHOLDING_TAX: 'enableWithholdingTax',
    PARTS_OR_LABOR: 'partsOrLabor',
    ENABLE_LABOR_TYPE: 'enableLaborType',
    LABOR_TYPE: 'laborType',
  };

  constructor(
    private _formBuilder: FormBuilder,
    private _percentagePipe: PercentagePipe,
    private _moneyService: MoneyService,
    private _translateService: TranslateService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  buildForm(
    documentItem: DocumentItem,
    taxes: List<Tax>,
    withholdingTax: WithholdingTax,
    separatePartsAndLabor: boolean
  ): FormGroup {
    const formKeys = this.FORM_KEYS;
    const hasDiscount = documentItem.hasDiscount();
    const discountAmount = documentItem.discountAmount;
    const discountPercentage = documentItem.discountPercentage;
    const isPart = documentItem.isPart();
    const disableLaborSection = separatePartsAndLabor && isPart;
    const unitType: ItemUnitTypesEnum = documentItem.unitType;
    const enableLaborType =
      unitType === ItemUnitTypesEnum.labor_days ||
      unitType === ItemUnitTypesEnum.labor_hours;
    const formGroup = {
      [formKeys.ENABLE_WITHHOLDING_TAX]: [documentItem.withholdingTaxApplies],
      [formKeys.PARTS_OR_LABOR]: [
        {
          value: isPart ? ItemUnitTypesEnum.parts : ItemUnitTypesEnum.labor,
          disabled: !separatePartsAndLabor,
        },
      ],
      [formKeys.ENABLE_LABOR_TYPE]: [
        {
          value: enableLaborType,
          disabled: disableLaborSection,
        },
      ],
      [formKeys.LABOR_TYPE]: [
        {
          value:
            isPart || unitType === ItemUnitTypesEnum.labor
              ? ItemUnitTypesEnum.labor_days
              : unitType,
          disabled: !enableLaborType || disableLaborSection,
        },
      ],
      [formKeys.ENABLE_MARKUP]: [false],
      [formKeys.MARKUP_TYPE]: [
        {
          value: MarkupTypesEnum.percentage,
          disabled: true,
        },
      ],
      [formKeys.MARKUP_PERCENTAGE]: [
        {
          value: 0,
          disabled: true,
        },
        CustomValidators.range([0, 100]),
      ],
      [formKeys.MARKUP_AMOUNT]: [
        {
          value: 0,
          disabled: true,
        },
        CustomValidators.range([0, 9999999999999]),
      ],
      [formKeys.ENABLE_DISCOUNT]: [hasDiscount],
      [formKeys.DISCOUNT_TYPE]: [
        {
          value: hasDiscount
            ? documentItem.discountType
            : DiscountTypesEnum.percentage,
          disabled: !hasDiscount,
        },
      ],
      [formKeys.DISCOUNT_PERCENTAGE]: [
        {
          value: discountPercentage,
          disabled: !hasDiscount,
        },
        CustomValidators.range([0, 100]),
      ],
      [formKeys.DISCOUNT_AMOUNT]: [
        {
          value: discountAmount,
          disabled: !hasDiscount,
        },
        CustomValidators.range([0, 9999999999999]),
      ],
    };

    taxes.forEach((tax: Tax) => {
      const taxId = tax.id;
      const [enableTaxKey, taxRateKey] = this.buildFormKeysForTax(taxId);
      const matchedAppliedTax = documentItem.appliedTaxes.find(
        (appliedTax: AppliedTax) => appliedTax.taxId === taxId
      );

      formGroup[enableTaxKey] = [!!matchedAppliedTax];
      formGroup[taxRateKey] = [
        {
          value: matchedAppliedTax ? matchedAppliedTax.rate : tax.firstRate,
          disabled: false,
        },
      ];
    });

    return this._formBuilder.group(formGroup);
  }

  buildDiscountSwitches(
    currencyCode: string,
    trackingObject: ITrackingObject,
    trackingScreenName: TrackingScreenNamesEnum
  ): List<IValueSwitch> {
    const currencyInfo: ICurrencyInfo =
      this._moneyService.getCurrencyInfo(currencyCode) ||
      this._moneyService.getCurrencyInfo();

    return List([
      {
        key: '%',
        value: DiscountTypesEnum.percentage,
        trackingObject: trackingObject,
        trackingAction: this._trackingActionBuilder.buildButtonTapped(
          'discount_to_percentage',
          trackingScreenName
        ),
      },
      {
        key: currencyInfo.symbol,
        value: DiscountTypesEnum.amount,
        trackingObject: trackingObject,
        trackingAction: this._trackingActionBuilder.buildButtonTapped(
          'discount_to_currency_amount',
          trackingScreenName
        ),
      },
    ]);
  }

  buildMarkupSwitches(
    currencyCode: string,
    trackingObject: ITrackingObject,
    trackingScreenName: TrackingScreenNamesEnum
  ): List<IValueSwitch> {
    const currencyInfo: ICurrencyInfo =
      this._moneyService.getCurrencyInfo(currencyCode) ||
      this._moneyService.getCurrencyInfo();

    return List([
      {
        key: '%',
        value: MarkupTypesEnum.percentage,
        trackingObject: trackingObject,
        trackingAction: this._trackingActionBuilder.buildButtonTapped(
          'markup_to_percentage',
          trackingScreenName
        ),
      },
      {
        key: currencyInfo.symbol,
        value: MarkupTypesEnum.amount,
        trackingObject: trackingObject,
        trackingAction: this._trackingActionBuilder.buildButtonTapped(
          'markup_to_currency_amount',
          trackingScreenName
        ),
      },
    ]);
  }

  buildFormKeysForTax(taxId: string): string[] {
    return [['enableTax', taxId].join('_'), ['taxRate', taxId].join('_')];
  }

  buildTaxRates(
    taxes: List<Tax>,
    locale: string
  ): { [taxId: string]: List<ISelectOption> } {
    const trackingValue = 'tax_rate';

    return (taxes || List([])).reduce((result, tax: Tax) => {
      const taxRates = tax.rates.map(
        (taxRate: TaxRate): ISelectOption => {
          const value = taxRate.value;

          return {
            key: this._percentagePipe.transform(value, locale, 3) as string,
            value,
            trackingValue,
          };
        }
      ) as List<ISelectOption>;

      let newTaxRates: List<ISelectOption> = taxRates;

      if (tax.autoApply) {
        const rateValues = {};

        newTaxRates = taxRates
          .push({
            key: this._percentagePipe.transform(0, locale, 0) as string,
            value: 0,
            trackingValue,
          })
          .filter((option: ISelectOption) => {
            const value = option.value;

            if (rateValues[value]) {
              return false;
            } else {
              rateValues[value] = true;

              return true;
            }
          }) as List<ISelectOption>;
      }

      result[tax.id] = newTaxRates;

      return result;
    }, {});
  }

  buildPartsOrLaborOptions(): List<ISelectOption> {
    return List([
      {
        key: this._translateService.instant('LABEL.PARTS'),
        value: ItemUnitTypesEnum.parts,
        trackingValue: 'parts',
      },
      {
        key: this._translateService.instant('LABEL.LABOR'),
        value: ItemUnitTypesEnum.labor,
        trackingValue: 'labor',
      },
    ]);
  }

  buildLaborTypeOptions(defaultToLabor: boolean): List<ISelectOption> {
    return List([
      {
        key: this._translateService.instant('LABEL.DAYS'),
        value: ItemUnitTypesEnum.labor_days,
        trackingValue: 'days',
      },
      {
        key: this._translateService.instant('LABEL.HOURS'),
        value: ItemUnitTypesEnum.labor_hours,
        trackingValue: 'hours',
      },
    ]);
  }

  buildDocumentItemProps(
    documentItemOptionsInfo: Map<string, any>,
    taxes: List<Tax>,
    separatePartsOrLabor: boolean
  ): Map<string, any> {
    const formKeys = this.FORM_KEYS;
    const appliedTaxes = [];

    (taxes || List([])).forEach((tax: Tax) => {
      const [enableTaxKey, taxRateKey] = this.buildFormKeysForTax(tax.id);

      if (documentItemOptionsInfo.get(enableTaxKey)) {
        appliedTaxes.push({
          [AppliedTax.KEYS.TAX_ID]: tax.id,
          [AppliedTax.KEYS.RATE]: parseFloat(
            documentItemOptionsInfo.get(taxRateKey)
          ),
        });
      }
    });

    const isPart =
      documentItemOptionsInfo.get(formKeys.PARTS_OR_LABOR, null) ===
      ItemUnitTypesEnum.parts;
    const enableLaborType = documentItemOptionsInfo.get(
      formKeys.ENABLE_LABOR_TYPE,
      false
    );
    const unitType: ItemUnitTypesEnum = (
      separatePartsOrLabor ? isPart : !enableLaborType
    )
      ? ItemUnitTypesEnum.parts
      : documentItemOptionsInfo.get(
          formKeys.LABOR_TYPE,
          ItemUnitTypesEnum.labor
        );

    const discountProps = {
      [Discount.KEYS.TYPE]: documentItemOptionsInfo.get(
        formKeys.ENABLE_DISCOUNT
      )
        ? documentItemOptionsInfo.get(formKeys.DISCOUNT_TYPE)
        : DiscountTypesEnum.no_discount,
      [Discount.KEYS.AMOUNT]:
        documentItemOptionsInfo.get(formKeys.DISCOUNT_AMOUNT) || 0,
      [Discount.KEYS.PERCENTAGE]:
        documentItemOptionsInfo.get(formKeys.DISCOUNT_PERCENTAGE) || 0,
    };
    const documentItemProps = {
      [DocumentItem.KEYS.DISCOUNT]: discountProps,
      [DocumentItem.KEYS.APPLIED_TAXES]: appliedTaxes,
      [DocumentItem.KEYS.UNIT_TYPE]: unitType,
      [DocumentItem.KEYS.WITHHOLDING_TAX_APPLIES]: documentItemOptionsInfo.get(
        formKeys.ENABLE_WITHHOLDING_TAX
      ),
    };

    return fromJS(documentItemProps);
  }

  buildTriggerTextKey(
    taxes: List<Tax>,
    showDiscount: boolean,
    documentSettings: DocumentPresetSettings
  ): string {
    if (taxes.size > 0) {
      let keyWithTax = showDiscount
        ? 'ACTION.TAX_DAYS_HOURS_DISCOUNT'
        : 'ACTION.TAX_DAYS_HOURS';

      if (documentSettings.separatePartsAndLabor) {
        keyWithTax = showDiscount
          ? 'ACTION.TAX_PARTS_LABOR_DISCOUNT'
          : 'ACTION.TAX_PARTS_LABOR';
      }

      const firstTaxName = taxes.first().name;

      return this._translateService.instant(keyWithTax, { tax: firstTaxName });
    }

    let keyWithoutTax = showDiscount
      ? 'ACTION.DAYS_HOURS_DISCOUNT'
      : 'ACTION.DAYS_HOURS';

    if (documentSettings.separatePartsAndLabor) {
      keyWithoutTax = showDiscount
        ? 'ACTION.PARTS_LABOR_DISCOUNT'
        : 'ACTION.PARTS_LABOR';
    }

    return this._translateService.instant(keyWithoutTax);
  }
}
